import { apiFetch } from "./client";

/**
 * Alle Unternehmen des aktuellen Benutzers abrufen
 * - Superadmins sehen ALLE
 * - Owner sehen nur ihre
 */
export async function getCompanies() {
  return apiFetch("/companies", { skipCompanyHeader: true });
}

/**
 * Details eines bestimmten Unternehmens abrufen
 */
export async function getCompanyById(companyId) {
  return apiFetch(`/companies/${companyId}`);
}

/**
 * Fuer die Verantwortlich-Auswahl berechtigte Mitglieder eines Unternehmens.
 * section: "topics" | "content"; channel optional (nur Content).
 */
export async function getCompanyAssignees(companyId, section = "topics", channel) {
  const params = new URLSearchParams({ section });
  if (channel) {
    params.set("channel", channel);
  }
  return apiFetch(`/companies/${companyId}/assignees?${params.toString()}`, {
    skipCompanyHeader: true,
  });
}

/**
 * Neues Unternehmen erstellen (nur Superadmins)
 */
export async function createCompany(name, vatId, ownerEmail) {
  return apiFetch("/companies", {
    method: "POST",
    body: JSON.stringify({
      name,
      vatId,
      ownerEmail,
    }),
  });
}

/**
 * Unternehmen löschen (nur Superadmins)
 */
export async function deleteCompany(companyId) {
  return apiFetch(`/companies/${companyId}`, {
    method: "DELETE",
  });
}

/**
 * Geplante Unternehmensloeschung abbrechen (nur Superadmins)
 */
export async function cancelCompanyDeletion(companyId) {
  return apiFetch(`/companies/${companyId}/cancel-deletion`, {
    method: "POST",
  });
}

/**
 * Rolle eines bestehenden Mitglieds aendern (nur Superadmins)
 */
export async function updateCompanyMemberRole(companyId, userId, role) {
  return apiFetch(`/companies/${companyId}/members/${userId}/role`, {
    method: "PUT",
    body: JSON.stringify({ role }),
  });
}

/**
 * Neues Mitglied zu Unternehmen hinzufügen
 */
export async function addCompanyMember(companyId, userEmail, role, permissions = {}) {
  return apiFetch(`/companies/${companyId}/members`, {
    method: "POST",
    body: JSON.stringify({
      userEmail,
      role,
      canViewReputation: Boolean(permissions.canViewReputation),
      canViewWikipedia: Boolean(permissions.canViewWikipedia),
      canViewLocations: Boolean(permissions.canViewLocations),
      canViewCompetitor: Boolean(permissions.canViewCompetitor),
      canViewAiOverview: Boolean(permissions.canViewAiOverview),
      canViewAiVisibility: Boolean(permissions.canViewAiVisibility),
      canViewContent: Boolean(permissions.canViewContent),
      reputationCountries: Array.isArray(permissions.reputationCountries)
        ? permissions.reputationCountries
        : null,
    }),
  });
}

/**
 * Dashboard-Berechtigungen eines Mitglieds aktualisieren
 */
export async function updateCompanyMemberPermissions(companyId, userId, permissions) {
  return apiFetch(`/companies/${companyId}/members/${userId}/permissions`, {
    method: "PUT",
    body: JSON.stringify({
      canViewReputation: Boolean(permissions.canViewReputation),
      canViewWikipedia: Boolean(permissions.canViewWikipedia),
      canViewLocations: Boolean(permissions.canViewLocations),
      canViewCompetitor: Boolean(permissions.canViewCompetitor),
      canViewAiOverview: Boolean(permissions.canViewAiOverview),
      canViewAiVisibility: Boolean(permissions.canViewAiVisibility),
      canViewContent: Boolean(permissions.canViewContent),
      canViewContentBlog: Boolean(permissions.canViewContentBlog),
      canViewContentLinkedin: Boolean(permissions.canViewContentLinkedin),
      canViewContentMeta: Boolean(permissions.canViewContentMeta),
      canViewContentYoutube: Boolean(permissions.canViewContentYoutube),
      canViewContentPress: Boolean(permissions.canViewContentPress),
      canViewContentOverview: Boolean(permissions.canViewContentOverview),
      canViewTopics: Boolean(permissions.canViewTopics),
      canViewSearch: Boolean(permissions.canViewSearch),
      reputationCountries: Array.isArray(permissions.reputationCountries)
        ? permissions.reputationCountries
        : null,
    }),
  });
}

/**
 * Loggt einen vom Nutzer initiierten Unternehmenswechsel
 */
export async function logCompanySwitch(companyId) {
  return apiFetch(`/companies/${companyId}/log-switch`, {
    method: "POST",
  });
}

/**
 * Mitglied aus Unternehmen entfernen
 */
export async function removeCompanyMember(companyId, userId) {
  return apiFetch(`/companies/${companyId}/members/${userId}`, {
    method: "DELETE",
  });
}

/**
 * Unternehmen aktualisieren (zukünftige Erweiterung)
 */
export async function updateCompany(companyId, updates) {
  return apiFetch(`/companies/${companyId}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });
}

/**
 * Unternehmens-Favicon aktualisieren.
 *
 * Wichtig: Google Cloud Armor blockiert Requests, die einen "data:"-URI
 * im Body enthalten (XSS-WAF-Regel). Wir senden daher den Base64-Teil
 * ohne den "data:<mime>;base64,"-Prefix und uebergeben den MIME-Type
 * separat. Das Backend rekonstruiert die Data-URL intern.
 *
 * Zum Loeschen: faviconDataUrl = null oder "" uebergeben.
 */
export async function updateCompanyFavicon(companyId, faviconDataUrl) {
  let payload;

  if (faviconDataUrl === null || faviconDataUrl === "") {
    payload = { faviconBase64: null };
  } else if (typeof faviconDataUrl === "string") {
    const match = faviconDataUrl.match(
      /^data:(image\/[a-zA-Z0-9+.-]+);base64,(.+)$/
    );
    if (match) {
      payload = { faviconMimeType: match[1], faviconBase64: match[2] };
    } else {
      // Fallback: an Backend weiterreichen, das wird die Validierung mit 400
      // ablehnen (gelangt aber moeglicherweise gar nicht erst durch die WAF).
      payload = { faviconDataUrl };
    }
  } else {
    payload = { faviconDataUrl };
  }

  return apiFetch(`/companies/${companyId}/favicon`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

/**
 * Loom-Video eines Unternehmens abrufen (inkl. Aktiv-Status).
 */
export async function getCompanyLoomVideo(companyId) {
  return apiFetch(`/companies/${companyId}/loom-video`);
}

/**
 * Loom-Video setzen/entfernen (nur Superadmins).
 * @param {string|null} loomVideoId - Video-ID, null/"" zum Entfernen.
 * @param {string|null} validUntil - "YYYY-MM-DD" oder null = unendlich.
 */
export async function updateCompanyLoomVideo(companyId, loomVideoId, validUntil) {
  return apiFetch(`/companies/${companyId}/loom-video`, {
    method: "PUT",
    body: JSON.stringify({ loomVideoId: loomVideoId || null, validUntil: validUntil || null }),
  });
}

/**
 * Content Infos (Marke, Website, Briefing, Sprache) fuer KI-Entwuerfe (Admins).
 */
export async function getCompanyContentInfo(companyId) {
  return apiFetch(`/companies/${companyId}/content-info`);
}

export async function updateCompanyContentInfo(companyId, payload) {
  return apiFetch(`/companies/${companyId}/content-info`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

/**
 * Domains eines Unternehmens abrufen
 */
export async function getCompanyDomains(companyId) {
  return apiFetch(`/companies/${companyId}/domains`);
}

/**
 * Domain hinzufuegen
 */
export async function addCompanyDomain(companyId, domain) {
  return apiFetch(`/companies/${companyId}/domains`, {
    method: "POST",
    body: JSON.stringify({ domain }),
  });
}

/**
 * Domain aktualisieren
 */
export async function updateCompanyDomain(companyId, domainId, domain) {
  return apiFetch(`/companies/${companyId}/domains/${domainId}`, {
    method: "PUT",
    body: JSON.stringify({ domain }),
  });
}

/**
 * Domain loeschen
 */
export async function deleteCompanyDomain(companyId, domainId) {
  return apiFetch(`/companies/${companyId}/domains/${domainId}`, {
    method: "DELETE",
  });
}

/**
 * Domain-Metriken abrufen
 */
export async function getCompanyDomainMetrics(companyId) {
  return apiFetch(`/companies/${companyId}/domain-metrics`);
}

/**
 * Domain-Metriken fuer Presseartikel abrufen
 */
export async function getCompanyPressDomainMetrics(companyId) {
  return apiFetch(`/companies/${companyId}/press-articles/domain-metrics`);
}

/**
 * Domain-Metriken aktualisieren (Superadmin)
 */
export async function refreshCompanyDomainMetrics(companyId) {
  return apiFetch(`/companies/${companyId}/domain-metrics/refresh`, {
    method: "POST",
  });
}

/**
 * Domain-Metriken fuer Presseartikel-URLs aktualisieren (Superadmin)
 */
export async function refreshCompanyPressDomainMetrics(companyId) {
  return apiFetch(`/companies/${companyId}/press-articles/domain-metrics/refresh`, {
    method: "POST",
  });
}

/**
 * Δ-Vergleich der Domain-Metriken (current vs. previous Snapshot)
 * @param {string} companyId
 * @param {{ compare?: 'previous' | '7d' }} [options]
 */
export async function getCompanyDomainMetricsDiff(companyId, { compare = "previous" } = {}) {
  return apiFetch(
    `/companies/${companyId}/domain-metrics/diff?compare=${encodeURIComponent(compare)}`,
    { cache: "no-store" }
  );
}

/**
 * Δ-Vergleich der Presseartikel-Domain-Metriken
 */
export async function getCompanyPressDomainMetricsDiff(companyId, { compare = "previous" } = {}) {
  return apiFetch(
    `/companies/${companyId}/press-articles/domain-metrics/diff?compare=${encodeURIComponent(compare)}`,
    { cache: "no-store" }
  );
}

/**
 * Wikipedia-Artikel eines Unternehmens abrufen
 */
export async function getCompanyWikipediaArticles(companyId) {
  return apiFetch(`/companies/${companyId}/wikipedia/articles`);
}

/**
 * Wikipedia-Artikel hinzufügen (Superadmin)
 */
export async function addCompanyWikipediaArticle(companyId, articleName, project) {
  return apiFetch(`/companies/${companyId}/wikipedia/articles`, {
    method: "POST",
    body: JSON.stringify({ articleName, project }),
  });
}

/**
 * Wikipedia-Artikel löschen (Superadmin)
 */
export async function deleteCompanyWikipediaArticle(companyId, articleId) {
  return apiFetch(`/companies/${companyId}/wikipedia/articles/${articleId}`, {
    method: "DELETE",
  });
}

/**
 * Diagramm-Farbe eines Wikipedia-Artikels setzen/aktualisieren
 * (Superadmin oder Wikipedia-Mitarbeiter). `color` als Hex-Wert (#RRGGBB)
 * oder null, um auf die Standardfarbe zurückzusetzen.
 */
export async function updateCompanyWikipediaArticleColor(companyId, articleId, color) {
  return apiFetch(
    `/companies/${companyId}/wikipedia/articles/${articleId}/color`,
    {
      method: "PATCH",
      body: JSON.stringify({ color }),
    }
  );
}

/**
 * Wikipedia-Pageviews im Zeitraum abrufen
 */
export async function getCompanyWikipediaPageviews(companyId, fromDate, toDate) {
  const params = new URLSearchParams();
  if (fromDate) {
    params.set("fromDate", fromDate);
  }
  if (toDate) {
    params.set("toDate", toDate);
  }

  const query = params.toString();
  return apiFetch(
    `/companies/${companyId}/wikipedia/pageviews${query ? `?${query}` : ""}`
  );
}

/**
 * Wikipedia-Pageviews historisch nachladen (Superadmin)
 */
export async function refreshCompanyWikipediaPageviews(companyId, fromDate, toDate) {
  return apiFetch(`/companies/${companyId}/wikipedia/pageviews/refresh`, {
    method: "POST",
    body: JSON.stringify({ fromDate, toDate }),
  });
}

/**
 * Wikipedia-Top-Editoren im Zeitraum abrufen
 */
export async function getCompanyWikipediaTopEditors(companyId, fromDate, toDate, limit = 1000) {
  const params = new URLSearchParams();
  if (fromDate) {
    params.set("fromDate", fromDate);
  }
  if (toDate) {
    params.set("toDate", toDate);
  }
  if (limit) {
    params.set("limit", String(limit));
  }

  const query = params.toString();
  return apiFetch(
    `/companies/${companyId}/wikipedia/top-editors${query ? `?${query}` : ""}`
  );
}

/**
 * Wikipedia-Top-Editoren historisch nachladen (Superadmin)
 */
export async function refreshCompanyWikipediaTopEditors(
  companyId,
  fromDate,
  toDate,
  limit = 1000
) {
  return apiFetch(`/companies/${companyId}/wikipedia/top-editors/refresh`, {
    method: "POST",
    body: JSON.stringify({ fromDate, toDate, limit }),
  });
}

/**
 * Wikipedia-Status (Freitext) einer Company laden
 */
export async function getCompanyWikipediaStatus(companyId) {
  return apiFetch(`/companies/${companyId}/wikipedia/status`);
}

/**
 * Wikipedia-Status (Freitext) einer Company speichern
 * (Superadmin oder Wikipedia-Mitarbeiter)
 */
export async function updateCompanyWikipediaStatus(companyId, status) {
  return apiFetch(`/companies/${companyId}/wikipedia/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
}

/**
 * Reputation-Status (Freitext) einer Company laden
 */
export async function getCompanyReputationStatus(companyId) {
  return apiFetch(`/companies/${companyId}/reputation/status`);
}

/**
 * Reputation-Status (Freitext) einer Company speichern (Superadmin only)
 */
export async function updateCompanyReputationStatus(companyId, status) {
  return apiFetch(`/companies/${companyId}/reputation/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
}

/**
 * Dashboard-Status (Freitext) einer Company laden
 */
export async function getCompanyDashboardStatus(companyId) {
  return apiFetch(`/companies/${companyId}/dashboard/status`);
}

/**
 * Dashboard-Status (Freitext) einer Company speichern (Superadmin only)
 */
export async function updateCompanyDashboardStatus(companyId, status) {
  return apiFetch(`/companies/${companyId}/dashboard/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
}

/**
 * Dashboard-Betreuer einer Company laden
 */
export async function getCompanyDashboardSupervisor(companyId) {
  return apiFetch(`/companies/${companyId}/dashboard/supervisor`);
}

/**
 * Dashboard-Betreuer einer Company speichern (Superadmin only)
 */
export async function updateCompanyDashboardSupervisor(companyId, status) {
  return apiFetch(`/companies/${companyId}/dashboard/supervisor`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
}

/**
 * Standorte-Status (Freitext) einer Company laden
 */
export async function getCompanyLocationsStatus(companyId) {
  return apiFetch(`/companies/${companyId}/locations/status`);
}

/**
 * Standorte-Status (Freitext) einer Company speichern (Superadmin only)
 */
export async function updateCompanyLocationsStatus(companyId, status) {
  return apiFetch(`/companies/${companyId}/locations/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
}

/**
 * Competitor-Status (Freitext) einer Company laden
 */
export async function getCompanyCompetitorStatus(companyId) {
  return apiFetch(`/companies/${companyId}/competitor/status`);
}

/**
 * Competitor-Status (Freitext) einer Company speichern (Superadmin only)
 */
export async function updateCompanyCompetitorStatus(companyId, status) {
  return apiFetch(`/companies/${companyId}/competitor/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
}

/**
 * Zugehörige Links eines Unternehmens abrufen
 */
export async function getCompanyLinks(companyId) {
  return apiFetch(`/companies/${companyId}/links`);
}

/**
 * Zugehörigen Link hinzufügen (Superadmin)
 */
export async function addCompanyLink(companyId, title, url) {
  return apiFetch(`/companies/${companyId}/links`, {
    method: "POST",
    body: JSON.stringify({ title, url }),
  });
}

/**
 * Zugehörigen Link löschen (Superadmin)
 */
export async function deleteCompanyLink(companyId, linkId) {
  return apiFetch(`/companies/${companyId}/links/${linkId}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Competitor-Dashboard: Plattformvergleich
// ---------------------------------------------------------------------------

/**
 * Liste aller Wettbewerber-Domains eines Unternehmens
 */
export async function getCompanyCompetitorDomains(companyId) {
  return apiFetch(`/companies/${companyId}/competitor-domains`);
}

/**
 * Wettbewerber-Domain hinzufuegen (Superadmin)
 */
export async function addCompanyCompetitorDomain(companyId, domain, tags = null) {
  return apiFetch(`/companies/${companyId}/competitor-domains`, {
    method: "POST",
    body: JSON.stringify({ domain, tags }),
  });
}

/**
 * Wettbewerber-Domain aktualisieren (Superadmin)
 */
export async function updateCompanyCompetitorDomain(companyId, domainId, domain, tags = null) {
  return apiFetch(`/companies/${companyId}/competitor-domains/${domainId}`, {
    method: "PUT",
    body: JSON.stringify({ domain, tags }),
  });
}

/**
 * Wettbewerber-Domain loeschen (Superadmin)
 */
export async function deleteCompanyCompetitorDomain(companyId, domainId) {
  return apiFetch(`/companies/${companyId}/competitor-domains/${domainId}`, {
    method: "DELETE",
  });
}

/**
 * Aktuelle Site-Explorer-Metriken aller Wettbewerber-Domains
 */
export async function getCompanyCompetitorDomainMetrics(companyId) {
  return apiFetch(`/companies/${companyId}/competitor-domain-metrics`);
}

/**
 * Diff-Vergleich der Wettbewerber-Domain-Metriken (current vs. previous)
 */
export async function getCompanyCompetitorDomainMetricsDiff(
  companyId,
  { compare = "previous" } = {}
) {
  return apiFetch(
    `/companies/${companyId}/competitor-domain-metrics/diff?compare=${encodeURIComponent(
      compare
    )}`,
    { cache: "no-store" }
  );
}

/**
 * Wettbewerber-Domain-Metriken aktualisieren (Superadmin)
 */
export async function refreshCompanyCompetitorDomainMetrics(companyId) {
  return apiFetch(`/companies/${companyId}/competitor-domain-metrics/refresh`, {
    method: "POST",
  });
}

/**
 * Top-10 organische Keywords pro Wettbewerber-Domain × Land
 * (Jüngster Snapshot pro Kombination.)
 */
export async function getCompanyCompetitorOrganicKeywords(companyId) {
  return apiFetch(`/companies/${companyId}/competitor-organic-keywords`);
}

/**
 * Diff-Vergleich der Konkurrenz-Organic-Keywords (Aggregate pro Kombination).
 */
export async function getCompanyCompetitorOrganicKeywordsDiff(
  companyId,
  { compare = "previous" } = {}
) {
  return apiFetch(
    `/companies/${companyId}/competitor-organic-keywords/diff?compare=${encodeURIComponent(
      compare
    )}`,
    { cache: "no-store" }
  );
}

/**
 * Konkurrenz-Organic-Keywords aktualisieren (Superadmin).
 * Ein Aufruf pro (domain × country) — Country-Codes werden aus
 * company_keywords abgeleitet.
 */
export async function refreshCompanyCompetitorOrganicKeywords(companyId) {
  return apiFetch(
    `/companies/${companyId}/competitor-organic-keywords/refresh`,
    { method: "POST" }
  );
}

// ---------------------------------------------------------------------------
// Einladungen
// ---------------------------------------------------------------------------

export async function listCompanyInvitations(companyId) {
  return apiFetch(`/companies/${companyId}/invitations`);
}

export async function createCompanyInvitation(
  companyId,
  email,
  role,
  permissions = {}
) {
  return apiFetch(`/companies/${companyId}/invitations`, {
    method: "POST",
    body: JSON.stringify({
      email,
      role,
      canViewReputation: Boolean(permissions.canViewReputation),
      canViewWikipedia: Boolean(permissions.canViewWikipedia),
      canViewLocations: Boolean(permissions.canViewLocations),
      canViewCompetitor: Boolean(permissions.canViewCompetitor),
      canViewAiOverview: Boolean(permissions.canViewAiOverview),
      reputationCountries: Array.isArray(permissions.reputationCountries)
        ? permissions.reputationCountries
        : null,
    }),
  });
}

export async function resendCompanyInvitation(companyId, invitationId) {
  return apiFetch(
    `/companies/${companyId}/invitations/${invitationId}/resend`,
    { method: "POST" }
  );
}

export async function revokeCompanyInvitation(companyId, invitationId) {
  return apiFetch(
    `/companies/${companyId}/invitations/${invitationId}`,
    { method: "DELETE" }
  );
}
